import { useState, useEffect } from 'react';
import { Calendar, momentLocalizer, View } from 'react-big-calendar';
import moment from 'moment';
import { format } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { fetchCalendarEvents, ClassEvent } from '@/lib/googleCalendar';
import { GOOGLE_CALENDAR_EMBED_URL, GOOGLE_CALENDAR_ID, GOOGLE_CALENDAR_ICAL_URL } from '@/lib/constants';

// Configure the localizer with moment
// Moment will use the browser's local timezone automatically
// This ensures all dates are displayed in the user's local timezone
const localizer = momentLocalizer(moment);

// Import ClassEvent type from googleCalendar module

// Helper function to get the date for a specific day of the week
const getDayOfWeek = (baseDate: Date, dayOfWeek: number): Date => {
  const date = new Date(baseDate);
  const currentDay = date.getDay();
  const diff = dayOfWeek - currentDay;
  date.setDate(date.getDate() + diff);
  return date;
};

// Helper function to generate recurring events based on actual schedule
const generateSampleEvents = (): ClassEvent[] => {
  const today = new Date();
  const events: ClassEvent[] = [];
  
  // Generate events for the next 8 weeks
  for (let week = 0; week < 8; week++) {
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() + (week * 7));
    
    // MONDAY
    const monday = getDayOfWeek(weekStart, 1);
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 6, 0),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 7, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 11, 0),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 12, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'Juniors 1 BJJ',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 16, 0),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 17, 0),
      resource: { instructor: 'Youth Coach', level: 'Juniors 1' },
    });
    events.push({
      title: 'TEEN BJJ',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 17, 10),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 18, 0),
      resource: { instructor: 'Coach Marvin', level: 'Teen' },
    });
    events.push({
      title: 'BJJ Drills',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 18, 10),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 19, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'BJJ - All Levels',
      start: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 19, 0),
      end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate(), 20, 0),
      resource: { instructor: 'Professor Andre', level: 'All Levels' },
    });
    
    // TUESDAY
    const tuesday = getDayOfWeek(weekStart, 2);
    events.push({
      title: 'BJJ NO GI',
      start: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 11, 0),
      end: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 12, 0),
      resource: { instructor: 'Professor Romualdo', level: 'All Levels' },
    });
    events.push({
      title: 'Juniors 1 BJJ',
      start: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 16, 35),
      end: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 17, 20),
      resource: { instructor: 'Youth Coach', level: 'Juniors 1' },
    });
    events.push({
      title: 'Juniors 2 BJJ',
      start: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 17, 30),
      end: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 18, 20),
      resource: { instructor: 'Youth Coach', level: 'Juniors 2' },
    });
    events.push({
      title: 'Sparring',
      start: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 18, 30),
      end: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 19, 20),
      resource: { instructor: 'Professor Andre', level: 'All Levels' },
    });
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 19, 30),
      end: new Date(tuesday.getFullYear(), tuesday.getMonth(), tuesday.getDate(), 20, 30),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    
    // WEDNESDAY
    const wednesday = getDayOfWeek(weekStart, 3);
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 6, 0),
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 7, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 11, 0),
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 12, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'Juniors 1 BJJ',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 16, 15),  // 4:15pm
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 17, 15),    // 5:15pm
      resource: { instructor: 'Youth Coach', level: 'Juniors 1' },
    });
    events.push({
      title: 'TEEN BJJ',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 17, 10),
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 18, 0),
      resource: { instructor: 'Coach Marvin', level: 'Teen' },
    });
    events.push({
      title: 'BJJ Drills',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 18, 10),
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 19, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'BJJ - All Levels',
      start: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 19, 0),
      end: new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate(), 20, 0),
      resource: { instructor: 'Professor Andre', level: 'All Levels' },
    });
    
    // THURSDAY
    const thursday = getDayOfWeek(weekStart, 4);
    events.push({
      title: 'BJJ NO GI',
      start: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 6, 0),
      end: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 7, 0),
      resource: { instructor: 'Professor Romualdo', level: 'All Levels' },
    });
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 11, 0),
      end: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 12, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'Juniors 2 BJJ',
      start: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 16, 35),
      end: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 17, 20),
      resource: { instructor: 'Youth Coach', level: 'Juniors 2' },
    });
    events.push({
      title: 'Situational BJJ Drills',
      start: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 18, 30),
      end: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 19, 20),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'BJJ - All Levels',
      start: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 19, 30),
      end: new Date(thursday.getFullYear(), thursday.getMonth(), thursday.getDate(), 20, 30),
      resource: { instructor: 'Professor Andre', level: 'All Levels' },
    });
    
    // FRIDAY
    const friday = getDayOfWeek(weekStart, 5);
    events.push({
      title: 'BJJ NO GI',
      start: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 11, 0),
      end: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 12, 0),
      resource: { instructor: 'Professor Romualdo', level: 'All Levels' },
    });
    events.push({
      title: 'TEEN BJJ',
      start: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 17, 10),
      end: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 18, 0),
      resource: { instructor: 'Coach Marvin', level: 'Teen' },
    });
    events.push({
      title: 'BJJ NO GI',
      start: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 19, 0),
      end: new Date(friday.getFullYear(), friday.getMonth(), friday.getDate(), 20, 0),
      resource: { instructor: 'Professor Romualdo', level: 'All Levels' },
    });
    
    // SATURDAY
    const saturday = getDayOfWeek(weekStart, 6);
    events.push({
      title: 'Juniors BJJ',
      start: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 10, 0),
      end: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 11, 0),
      resource: { instructor: 'Youth Coach', level: 'Juniors' },
    });
    events.push({
      title: 'BJJ All Levels',
      start: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 11, 0),
      end: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 12, 0),
      resource: { instructor: 'Professor Andre', level: 'All Levels' },
    });
    events.push({
      title: 'JUDO for Jiu-Jitsu',
      start: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 12, 15),
      end: new Date(saturday.getFullYear(), saturday.getMonth(), saturday.getDate(), 13, 5),
      resource: { instructor: 'Professor Gerson', level: 'All Levels' },
    });
    
    // SUNDAY
    const sunday = getDayOfWeek(weekStart, 0);
    events.push({
      title: 'BJJ Fundamentals',
      start: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 9, 0),
      end: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 10, 0),
      resource: { instructor: 'Professor Marcelo', level: 'Beginner' },
    });
    events.push({
      title: 'OPEN MAT',
      start: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 17, 0),
      end: new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 18, 30),
      resource: { instructor: 'Open', level: 'All Levels' },
    });
  }
  
  return events;
};

// Sample events - in production, these would come from an API
// You can replace this with actual calendar data from your backend
const sampleEvents: ClassEvent[] = generateSampleEvents();

interface ClassCalendarProps {
  events?: ClassEvent[];
}

export default function ClassCalendar({ events: propEvents }: ClassCalendarProps) {
  // Default to day view on mobile, week view on desktop (but allow user to change)
  const [currentView, setCurrentView] = useState<View>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return 'day';
    }
    return 'week';
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState<ClassEvent[]>(propEvents || []);
  const [isLoading, setIsLoading] = useState(!propEvents);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ClassEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Fetch events from Google Calendar on mount and when date changes
  useEffect(() => {
    // If events are provided as props, use them
    if (propEvents) {
      setCalendarEvents(propEvents);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch from Google Calendar
    const loadEvents = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Calculate date range based on current view
        const now = new Date();
        const timeMin = new Date(now);
        timeMin.setDate(timeMin.getDate() - 7); // Start 7 days ago
        
        const timeMax = new Date(now);
        timeMax.setDate(timeMax.getDate() + 60); // End 60 days from now
        
        // Try to get iCal URL from embed URL if available
        // But prioritize GOOGLE_CALENDAR_ICAL_URL if it's set
        let icalUrl: string | undefined;
        if (!GOOGLE_CALENDAR_ICAL_URL && GOOGLE_CALENDAR_EMBED_URL) {
          // Extract calendar ID from embed URL and convert to iCal format
          // Google Calendar embed URLs look like: https://calendar.google.com/calendar/embed?src=CALENDAR_ID
          const match = GOOGLE_CALENDAR_EMBED_URL.match(/src=([^&]+)/);
          if (match) {
            const calendarId = decodeURIComponent(match[1]);
            // Convert to iCal public feed URL
            icalUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(calendarId)}/public/basic.ics`;
            console.log('Extracted iCal URL from embed URL:', icalUrl);
          }
        } else if (GOOGLE_CALENDAR_ICAL_URL) {
          console.log('Using GOOGLE_CALENDAR_ICAL_URL from environment');
          icalUrl = GOOGLE_CALENDAR_ICAL_URL;
        }
        
        const events = await fetchCalendarEvents(icalUrl, timeMin, timeMax);
        
        if (events.length > 0) {
          console.log(`Loaded ${events.length} events from Google Calendar`);
          setCalendarEvents(events);
        } else {
          // Check if we have calendar configuration
          const hasConfig = GOOGLE_CALENDAR_EMBED_URL || GOOGLE_CALENDAR_ID || GOOGLE_CALENDAR_ICAL_URL;
          if (hasConfig) {
            console.warn('Google Calendar is configured but no events were returned. Check your calendar settings and ensure the calendar is public.');
            setError('No events found in Google Calendar. Please check your calendar settings.');
            // Don't use sample events if calendar is configured - show error instead
            setCalendarEvents([]);
          } else {
            console.warn('No Google Calendar configuration found. Using sample events. To connect your calendar, set VITE_GOOGLE_CALENDAR_ICAL_URL or VITE_GOOGLE_CALENDAR_ID and VITE_GOOGLE_CALENDAR_API_KEY in your .env file.');
            setCalendarEvents(sampleEvents);
          }
        }
      } catch (err) {
        console.error('Error loading calendar events:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load calendar events';
        setError(`Failed to load calendar events: ${errorMessage}`);
        
        // Only use sample events if no calendar is configured
        const hasConfig = GOOGLE_CALENDAR_EMBED_URL || GOOGLE_CALENDAR_ID || GOOGLE_CALENDAR_ICAL_URL;
        if (!hasConfig) {
          console.warn('No calendar configuration found. Using sample events.');
          setCalendarEvents(sampleEvents);
        } else {
          setCalendarEvents([]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadEvents();
  }, [propEvents]);

  // Reload events when navigating to different dates
  useEffect(() => {
    if (propEvents) return; // Don't reload if using prop events
    
    const loadEvents = async () => {
      const timeMin = new Date(currentDate);
      timeMin.setDate(timeMin.getDate() - 7);
      
      const timeMax = new Date(currentDate);
      timeMax.setDate(timeMax.getDate() + 60);
      
      let icalUrl: string | undefined;
      if (GOOGLE_CALENDAR_EMBED_URL) {
        const match = GOOGLE_CALENDAR_EMBED_URL.match(/src=([^&]+)/);
        if (match) {
          const calendarId = decodeURIComponent(match[1]);
          icalUrl = `https://calendar.google.com/calendar/ical/${encodeURIComponent(calendarId)}/public/basic.ics`;
        }
      }
      
      try {
        const events = await fetchCalendarEvents(icalUrl, timeMin, timeMax);
        if (events.length > 0) {
          console.log(`Reloaded ${events.length} events from Google Calendar`);
          setCalendarEvents(events);
        } else {
          const hasConfig = GOOGLE_CALENDAR_EMBED_URL || GOOGLE_CALENDAR_ID || GOOGLE_CALENDAR_ICAL_URL;
          if (hasConfig) {
            console.warn('No events returned from Google Calendar on reload');
          }
        }
      } catch (err) {
        console.error('Error reloading calendar events:', err);
      }
    };

    loadEvents();
  }, [currentDate, propEvents]);

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    // Handle slot selection if needed (e.g., for booking)
    console.log('Selected slot:', start, end);
  };

  const handleSelectEvent = (event: ClassEvent) => {
    setSelectedEvent(event);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedEvent(null);
  };

  const eventStyleGetter = (event: ClassEvent) => {
    const level = event.resource?.level || 'All Levels';
    let backgroundColor = '#1F4788'; // primary color
    let borderColor = '#16335F'; // primary-dark

    // Different colors for different class types
    if (level === 'Beginner') {
      backgroundColor = '#2E5BA3'; // primary-light
      borderColor = '#1F4788';
    } else if (level === 'Teen' || level === 'Juniors' || level === 'Juniors 1' || level === 'Juniors 2') {
      backgroundColor = '#2E5BA3'; // primary-light for youth programs
      borderColor = '#1F4788';
    } else if (level === 'Advanced') {
      backgroundColor = '#16335F'; // primary-dark
      borderColor = '#1F4788';
    }

    return {
      style: {
        backgroundColor,
        borderColor,
        borderWidth: '2px',
        borderRadius: '8px',
        color: 'white',
        padding: '4px 8px',
        fontSize: '14px',
        fontWeight: '600',
      },
    };
  };

  return (
    <div className="w-full h-full">
      <style>{`
        .rbc-calendar {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          background: white;
          border-radius: 12px;
        }
        
        .rbc-header {
          padding: 16px 12px;
          font-weight: 600;
          font-size: 14px;
          color: #171717;
          border-bottom: 2px solid #E5E5E5;
          background: #FAFAFA;
        }
        
        .rbc-today {
          background-color: rgba(31, 71, 136, 0.05);
        }
        
        .rbc-off-range-bg {
          background: #FAFAFA;
        }
        
        .rbc-time-slot {
          border-top: 1px solid #F5F5F5;
        }
        
        .rbc-time-header-content {
          border-left: 1px solid #E5E5E5;
        }
        
        .rbc-time-content {
          border-top: 2px solid #E5E5E5;
        }
        
        .rbc-day-slot .rbc-time-slot {
          border-top: 1px solid #F5F5F5;
        }
        
        .rbc-time-header {
          min-width: 60px;
        }
        
        .rbc-time-header-content {
          min-width: 0;
        }
        
        @media (min-width: 1024px) {
          .rbc-time-header {
            min-width: 80px;
          }
          
          .rbc-header {
            padding: 16px 16px;
          }
        }
        
        .rbc-time-header-gutter,
        .rbc-time-gutter {
          background: #FAFAFA;
          border-right: 1px solid #E5E5E5;
        }
        
        .rbc-toolbar {
          padding: 20px 24px;
          background: white;
          border-bottom: 2px solid #E5E5E5;
          margin-bottom: 0;
        }
        
        .rbc-toolbar button {
          color: #525252;
          border: 1px solid #E5E5E5;
          background: white;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 500;
          font-size: 14px;
          transition: all 0.2s;
        }
        
        .rbc-toolbar button:hover {
          background: #F5F5F5;
          border-color: #1F4788;
          color: #1F4788;
        }
        
        .rbc-toolbar button.rbc-active {
          background: #1F4788;
          color: white;
          border-color: #1F4788;
        }
        
        .rbc-toolbar-label {
          font-weight: 700;
          font-size: 18px;
          color: #171717;
        }
        
        .rbc-event {
          border-radius: 8px;
          padding: 4px 8px;
          min-height: 20px;
          display: flex;
          align-items: center;
        }
        
        .rbc-event-content {
          font-weight: 600;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        
        /* Month view specific event styling */
        .rbc-month-view .rbc-event {
          padding: 2px 6px;
          margin: 1px 0;
          min-height: 18px;
          font-size: 12px;
          width: 100%;
          display: block;
          position: relative;
          z-index: 1;
        }
        
        .rbc-month-view .rbc-event-content {
          font-size: 12px;
          line-height: 1.2;
          display: block;
        }
        
        .rbc-day-slot .rbc-event {
          margin-bottom: 2px;
        }
        
        /* Ensure events are visible in month cells */
        .rbc-date-cell {
          position: relative;
        }
        
        .rbc-day-bg + .rbc-events-container {
          margin-top: 4px;
        }
        
        .rbc-month-view .rbc-events-container {
          margin-top: 4px;
          min-height: 20px;
          position: relative;
          z-index: 1;
        }
        
        .rbc-day-slot .rbc-event {
          margin-bottom: 2px;
        }
        
        .rbc-current-time-indicator {
          background-color: #1F4788;
        }
        
        .rbc-show-more {
          background-color: rgba(31, 71, 136, 0.1);
          color: #1F4788;
          border-radius: 4px;
          padding: 2px 6px;
          font-weight: 600;
          font-size: 12px;
        }
        
        .rbc-show-more:hover {
          background-color: rgba(31, 71, 136, 0.2);
        }
        
        .rbc-month-view {
          border: 1px solid #E5E5E5;
          border-radius: 8px;
        }
        
        .rbc-month-row {
          border-bottom: 1px solid #E5E5E5;
        }
        
        .rbc-day-bg {
          border-right: 1px solid #E5E5E5;
        }
        
        .rbc-date-cell {
          padding: 8px;
          text-align: right;
        }
        
        .rbc-date-cell > a {
          color: #525252;
          font-weight: 600;
        }
        
        .rbc-date-cell.rbc-now > a {
          color: #1F4788;
          font-weight: 700;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          .rbc-calendar {
            font-size: 12px;
          }
          
          .rbc-toolbar {
            padding: 12px 8px;
            flex-wrap: wrap;
            gap: 8px;
          }
          
          .rbc-toolbar button {
            padding: 10px 12px;
            font-size: 12px;
            min-height: 44px;
            flex: 1 1 auto;
          }
          
          .rbc-toolbar-label {
            font-size: 14px;
            width: 100%;
            text-align: center;
            margin: 8px 0;
          }
          
          .rbc-header {
            padding: 12px 4px;
            font-size: 11px;
          }
          
          .rbc-time-header-gutter,
          .rbc-time-gutter {
            min-width: 50px;
            font-size: 11px;
          }
          
          .rbc-event {
            font-size: 11px;
            padding: 2px 4px;
            border-radius: 4px;
          }
          
          .rbc-day-slot {
            min-height: 60px;
          }
          
          .rbc-time-slot {
            min-height: 40px;
          }
          
          .rbc-time-content {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          /* Week view on mobile - make it horizontally scrollable */
          .rbc-time-view {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .rbc-time-header {
            min-width: 600px;
          }
          
          .rbc-time-content {
            min-width: 600px;
          }
          
          /* Month view on mobile */
          .rbc-month-view {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }
          
          .rbc-month-row {
            min-width: 100%;
          }
          
          .rbc-header {
            min-width: 80px;
            flex: 1 1 0;
          }
        }
        
        /* Very small screens */
        @media (max-width: 640px) {
          .rbc-toolbar button {
            padding: 8px 10px;
            font-size: 11px;
          }
          
          .rbc-header {
            padding: 10px 2px;
            font-size: 10px;
            min-width: 60px;
          }
          
          .rbc-event {
            font-size: 10px;
            padding: 1px 3px;
            min-height: 16px;
          }
          
          /* Month view events on mobile */
          .rbc-month-view .rbc-event {
            font-size: 10px;
            padding: 2px 4px;
            min-height: 16px;
            margin: 1px 0;
            display: block;
          }
          
          .rbc-month-view .rbc-event-content {
            font-size: 10px;
            display: block;
          }
          
          /* Ensure month view events container is visible */
          .rbc-month-view .rbc-events-container {
            margin-top: 4px;
            min-height: 20px;
          }
          
          .rbc-time-header {
            min-width: 500px;
          }
          
          .rbc-time-content {
            min-width: 500px;
          }
        }
      `}</style>
      
      {isLoading && (
        <div className="flex items-center justify-center h-[800px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
            <p className="text-gray-600">Loading calendar events...</p>
          </div>
        </div>
      )}
      {error && !isLoading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-yellow-800 text-sm">{error}</p>
        </div>
      )}
      <Calendar
        localizer={localizer}
        events={calendarEvents}
        startAccessor="start"
        endAccessor="end"
        view={currentView}
        onView={setCurrentView}
        date={currentDate}
        onNavigate={setCurrentDate}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        selectable
        eventPropGetter={eventStyleGetter}
        style={{ height: 'auto', minHeight: '600px' }}
        className="min-h-[600px] md:min-h-[800px]"
        views={['month', 'week', 'day']}
        defaultView="week"
        step={60}
        timeslots={1}
        min={new Date(2024, 0, 1, 5, 0)}
        max={new Date(2024, 0, 1, 22, 0)}
        formats={{
          timeGutterFormat: 'h:mm a',
          eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
            `${format(start, 'h:mm a')} - ${format(end, 'h:mm a')}`,
          dayFormat: 'ddd M/D',
          dayHeaderFormat: 'dddd, MMMM D',
          dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }) =>
            `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`,
          monthHeaderFormat: 'MMMM YYYY',
        }}
      />
      
      {/* Event Details Modal */}
      {isModalOpen && selectedEvent && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
          onClick={closeModal}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-primary to-primary-dark h-2"></div>
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.title}</h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Event Details */}
              <div className="space-y-4">
                {/* Date and Time */}
                <div className="flex items-start gap-3">
                  <div className="bg-primary/10 p-2 rounded-lg">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-700">Date & Time</p>
                    {selectedEvent.start && (
                      <p className="text-gray-600">
                        {format(selectedEvent.start, 'EEEE, MMMM d, yyyy')}
                      </p>
                    )}
                    {selectedEvent.start && selectedEvent.end && (
                      <p className="text-gray-600">
                        {format(selectedEvent.start, 'h:mm a')} - {format(selectedEvent.end, 'h:mm a')}
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Duration */}
                {selectedEvent.start && selectedEvent.end && (
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Duration</p>
                      <p className="text-gray-600">
                        {Math.round((selectedEvent.end.getTime() - selectedEvent.start.getTime()) / (1000 * 60))} minutes
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Level */}
                {selectedEvent.resource?.level && (
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Level</p>
                      <p className="text-gray-600">{selectedEvent.resource.level}</p>
                    </div>
                  </div>
                )}
                
                {/* Instructor */}
                {selectedEvent.resource?.instructor && (
                  <div className="flex items-start gap-3">
                    <div className="bg-primary/10 p-2 rounded-lg">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-700">Instructor</p>
                      <p className="text-gray-600">{selectedEvent.resource.instructor}</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={closeModal}
                  className="w-full px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

